// Compress image file to a JPEG or keep PNG (for transparent logos/favicons).
// Returns a data URL. Used by banners, logo and favicon uploaders.
export const readAndCompressImage = (file, opts = {}) => new Promise((resolve, reject) => {
  const { maxW = 1600, quality = 0.85, format = 'auto' } = opts;
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      // Detect transparent PNG (or forced format=png) → keep PNG to preserve alpha.
      let mime = 'image/jpeg';
      if (format === 'png' || (format === 'auto' && file.type === 'image/png')) mime = 'image/png';
      const dataUrl = canvas.toDataURL(mime, quality);
      resolve(dataUrl);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

// Auto-detect the dominant background color by sampling the 4 corners of the image.
// Returns { r, g, b } or null if corners are too varied to have a solid background.
const detectBackgroundColor = (ctx, w, h) => {
  const samples = [
    ctx.getImageData(0, 0, 1, 1).data,
    ctx.getImageData(w - 1, 0, 1, 1).data,
    ctx.getImageData(0, h - 1, 1, 1).data,
    ctx.getImageData(w - 1, h - 1, 1, 1).data,
  ];
  // Average corners; require all 4 to be within 40 units to consider it a background
  const avg = [0, 0, 0];
  for (const s of samples) { avg[0] += s[0]; avg[1] += s[1]; avg[2] += s[2]; }
  avg[0] /= 4; avg[1] /= 4; avg[2] /= 4;
  const maxDist = samples.reduce((m, s) => {
    const d = Math.sqrt((s[0] - avg[0]) ** 2 + (s[1] - avg[1]) ** 2 + (s[2] - avg[2]) ** 2);
    return Math.max(m, d);
  }, 0);
  if (maxDist > 40) return null;
  return { r: Math.round(avg[0]), g: Math.round(avg[1]), b: Math.round(avg[2]) };
};

// Removes a background color from a data URL using chroma-keying with smooth edges.
// Automatically detects the background from image corners; if it can't detect a solid
// background, returns the original data URL unchanged.
// Returns a PNG data URL (transparency requires PNG).
export const removeBackgroundFromDataUrl = (dataUrl, tolerance = 32) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onerror = reject;
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const target = detectBackgroundColor(ctx, canvas.width, canvas.height);
    if (!target) {
      // Complex background — leave the image alone.
      resolve(dataUrl);
      return;
    }
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;
    const t2 = tolerance * tolerance;
    const outer = tolerance * 2;
    const outer2 = outer * outer;
    for (let i = 0; i < data.length; i += 4) {
      const dr = data[i] - target.r;
      const dg = data[i + 1] - target.g;
      const db = data[i + 2] - target.b;
      const dist2 = dr * dr + dg * dg + db * db;
      if (dist2 <= t2) {
        data[i + 3] = 0;
      } else if (dist2 <= outer2) {
        // Smooth edge (feathering)
        const d = Math.sqrt(dist2);
        const alpha = Math.round(255 * ((d - tolerance) / tolerance));
        data[i + 3] = Math.min(data[i + 3], alpha);
      }
    }
    ctx.putImageData(imgData, 0, 0);
    resolve(canvas.toDataURL('image/png'));
  };
  img.src = dataUrl;
});

export default readAndCompressImage;
